/**
 * KAN-810: filters of the page "Лиды по теплоте": level chips with counts
 * (multi-select), manager, "Передан: все/да/нет" and search.
 * KAN-927: "Всего: N" after the chips: leads under the other filters, every level.
 * KAN-883: "Появился" - day the lead first wrote (quick buttons and calendar, "Всё время" by default).
 */
import { Search } from 'lucide-react';
import { CrmPeriodControl } from '@/components/crm/CrmPeriodControl';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';
import { useManagers } from '@/hooks/useManagers';
import type { LeadFiltersState } from '@/hooks/useLeadTemperature';
import {
  LEAD_TEMPERATURE_LABELS,
  LEAD_TEMPERATURE_LEVELS,
  leadTemperatureStyle,
  leadTemperatureTotal,
} from '@/lib/leadTemperature';
import { LEAD_DAY_CHOICES } from '@/lib/leadDayPeriod';
import type { LeadTemperatureCounts, LeadTransferredFilter } from '@/types/leadTemperature';

interface LeadFiltersBarProps {
  filters: LeadFiltersState;
  counts: LeadTemperatureCounts | undefined;
  /** KAN-927: total from the backend; undefined: sum of counts. */
  total?: number;
}

const TRANSFERRED_OPTIONS: Array<{ value: LeadTransferredFilter; label: string }> = [
  { value: 'all', label: 'Передан: все' },
  { value: 'yes', label: 'Передан: да' },
  { value: 'no', label: 'Передан: нет' },
];

export function LeadFiltersBar({ filters, counts, total }: LeadFiltersBarProps) {
  const { data: managers = [] } = useManagers('true');
  const managerOptions = [
    { value: '', label: 'Все менеджеры' },
    { value: 'none', label: 'Без менеджера' },
    ...managers.map((m) => ({ value: m.id, label: m.full_name })),
  ];

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex flex-wrap gap-2" role="group" aria-label="Теплота">
        {LEAD_TEMPERATURE_LEVELS.map((level) => {
          const on = filters.levels.includes(level);
          const pill = leadTemperatureStyle(level)?.pill ?? '';
          return (
            <button
              key={level}
              type="button"
              data-testid={`lead-level-chip-${level}`}
              aria-pressed={on}
              onClick={() => filters.toggleLevel(level)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium transition ${
                on ? `${pill} border-transparent` : 'border-gray-200 bg-white text-gray-500'
              }`}
            >
              {LEAD_TEMPERATURE_LABELS[level]}
              <span className="tabular-nums">{counts?.[level] ?? 0}</span>
            </button>
          );
        })}
        {counts && (
          <span data-testid="lead-temperature-total" className="self-center text-sm text-gray-600">
            Всего: <span className="tabular-nums font-medium text-gray-900">{leadTemperatureTotal(counts, total)}</span>
          </span>
        )}
      </div>
      <div className="w-48">
        <NativeSelect
          aria-label="Менеджер"
          value={filters.manager}
          onChange={(e) => filters.setManager(e.target.value)}
          options={managerOptions}
          className="h-9"
        />
      </div>
      <div className="w-40">
        <NativeSelect
          aria-label="Передан"
          value={filters.transferred}
          onChange={(e) => filters.setTransferred(e.target.value as LeadTransferredFilter)}
          options={TRANSFERRED_OPTIONS}
          className="h-9"
        />
      </div>
      <CrmPeriodControl
        label="Появился"
        choices={LEAD_DAY_CHOICES}
        range={filters.leadDay.range}
        onChange={filters.leadDay.setRange}
        onAllTime={filters.leadDay.setAllTime}
        testId="lead-temperature-period"
        showLabel
      />
      <div className="relative w-full sm:w-64">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <Input
          value={filters.search}
          onChange={(e) => filters.setSearch(e.target.value)}
          placeholder="Имя, компания, телефон"
          aria-label="Поиск лидов"
          className="h-9 pl-8"
        />
      </div>
    </div>
  );
}
